import { apiRequest } from './queryClient';
import type {
  ProjectSkillConfigSnapshot,
  SkillListFilters,
  SkillSummary,
  TenantSkillOverrides,
} from '@shared/skills';

export interface CatalogResponse {
  data: SkillSummary[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
}

export interface ProjectSkillsParams extends Partial<SkillListFilters> {
  projectId: string | number;
  page?: number;
  pageSize?: number;
}

export const fetchSkillsCatalog = async (
  params: Partial<{ search: string; page: number; pageSize: number }>,
): Promise<CatalogResponse> => {
  const query = new URLSearchParams();
  if (params.search) query.set('search', params.search);
  if (params.page) query.set('page', String(params.page));
  if (params.pageSize) query.set('pageSize', String(params.pageSize));
  const qs = query.toString();
  const url = qs ? `/api/v2/skills?${qs}` : '/api/v2/skills';
  return await apiRequest<CatalogResponse>('GET', url);
};

export const fetchProjectSkills = async (
  params: ProjectSkillsParams,
): Promise<ProjectSkillConfigSnapshot> => {
  const query = new URLSearchParams();
  if (params.search) query.set('search', params.search);
  if (params.status) query.set('status', params.status);
  if (params.category) query.set('category', params.category);
  if (params.page) query.set('page', String(params.page));
  if (params.pageSize) query.set('pageSize', String(params.pageSize));
  const qs = query.toString();
  const url = qs
    ? `/api/v2/skills/project/${params.projectId}?${qs}`
    : `/api/v2/skills/project/${params.projectId}`;
  return await apiRequest<ProjectSkillConfigSnapshot>('GET', url);
};

export const toggleProjectSkill = async (input: {
  projectId: string | number;
  skillId: string;
  enabled: boolean;
  version: number;
  reason?: string;
}) => {
  return await apiRequest('PUT', `/api/v2/skills/project/${input.projectId}/${input.skillId}`, {
    enabled: input.enabled,
    version: input.version,
    reason: input.reason,
  });
};

export const bulkToggleProjectSkills = async (input: {
  projectId: string | number;
  enabled: boolean;
  version: number;
  skillIds?: string[];
  applyAll?: boolean;
  reason?: string;
}) => {
  return await apiRequest('PUT', `/api/v2/skills/project/${input.projectId}/bulk`, {
    enabled: input.enabled,
    version: input.version,
    skillIds: input.skillIds,
    applyAll: input.applyAll,
    reason: input.reason,
  } satisfies Partial<SkillTogglePayload> & { enabled: boolean; version: number });
};

export const exportProjectSkills = async (
  projectId: string | number,
): Promise<Blob> => {
  const response = await fetch(`/api/v2/skills/project/${projectId}/export`, {
    credentials: 'include',
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Failed to export skills (${response.status})`);
  }
  return await response.blob();
};

export const fetchTenantConfig = async (
  tenantId: string,
): Promise<TenantSkillOverrides> => {
  return await apiRequest<TenantSkillOverrides>(
    'GET',
    `/api/v2/skills/tenant/${tenantId}`,
  );
};

export const updateTenantConfig = async (input: {
  tenantId: string;
  version: number;
  reason?: string;
  skills: Record<string, boolean | null | undefined>;
}) => {
  return await apiRequest<TenantSkillOverrides>(
    'PUT',
    `/api/v2/skills/tenant/${input.tenantId}`,
    input,
  );
};

interface SkillTogglePayload {
  enabled: boolean;
  version: number;
  reason?: string;
  skillIds?: string[];
  applyAll?: boolean;
}
